import { RealtimeClient } from "../../lib/realtimeClient.js";
import { TextCourseClient } from "../../lib/textCourseClient.js";

function browserTimeZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; }
  catch { return undefined; }
}

export function controls(options = {}) {
  return {
    instructions: options.instructions || options.systemPrompt || "",
    thinking: options.thinking !== false,
    voice: options.voice || "Chelsie",
    realtime_mode: options.realtimeMode || "chat",
  };
}

/** Same event protocol as the original client, authenticated before onopen.
 * No audio, course controls or credentials are sent before a one-use ticket.
 */
export class TicketSocket {
  constructor(api, body, { WebSocketImpl = WebSocket, locationImpl = location } = {}) {
    this.api = api;
    this.readyState = 0;
    this.socket = null;
    this.timer = null;
    this.timeout = null;
    this.heartbeat = null;
    this.lastServerError = null;
    this.open(api, body, WebSocketImpl, locationImpl).catch(error => this.fail(error));
  }
  get bufferedAmount() { return this.socket?.bufferedAmount || 0; }
  async open(api, body, WebSocketImpl, where) {
    const conversation = await api.request("/demo/sessions", { method: "POST", body });
    if (this.readyState === 3) return;
    const ticket = await api.request("/realtime-tickets", { method: "POST", body: { conversation_id: conversation.id } });
    if (this.readyState === 3) return;
    const socket = new WebSocketImpl(`${where.protocol === "https:" ? "wss" : "ws"}://${where.host}/api/v1/demo/realtime`);
    this.socket = socket;
    this.timeout = setTimeout(() => this.fail(Object.assign(
      new Error("实时连接建立超时，请检查网络后重试"), { code: "connection_timeout" })), 30000);
    socket.onopen = () => {
      if (this.readyState === 3) return;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => this.fail(Object.assign(
        new Error("连接已建立，但会话验证超时，请重试"), { code: "auth_timeout" })), 10000);
      socket.send(JSON.stringify({ type: "auth", ticket: ticket.ticket }));
    };
    socket.onmessage = event => {
      let message;
      try { message = JSON.parse(event.data); } catch { this.fail(new Error("Invalid server event")); return; }
      if (message.type === "auth.ok") {
        clearTimeout(this.timer);
        clearTimeout(this.timeout);
        this.timer = setTimeout(() => this.refresh(), Math.max(1000, message.expires_at * 1000 - Date.now() - 60000));
        return;
      }
      if (message.type === "session.ready") {
        if (this.readyState === 0) {
          this.readyState = 1;
          this.heartbeat = setInterval(() => {
            try { this.send(JSON.stringify({ type: "ping" })); }
            catch (error) { this.fail(error); }
          }, 20000);
          this.onopen?.();
        }
        return;
      }
      if (message.type === "pong") return;
      if (message.type === "error" && this.readyState === 0) {
        const error = message.error;
        this.fail(Object.assign(new Error(error?.message || "会话连接失败"), {
          code: error?.code || error?.type,
          trace_id: error?.trace_id,
        })); return;
      }
      if (message.type === "error") this.lastServerError = message.error;
      if (message.type === "response.created") this.lastServerError = null;
      // Original clients receive session.created only after their own config.
      if (this.readyState === 1) {
        this.onmessage?.(event);
        if (message.type === 'agent.update' && ['session.start','session.archive_queued','session.finish','session.interrupt'].includes(message.kind)) this.onHistoryChange?.();
      }
    };
    socket.onerror = () => this.fail(new Error("连接失败，请重试"));
    socket.onclose = event => {
      if (this.readyState === 3) return;
      const closeCode = event?.code ?? 1006;
      const previous = this.lastServerError;
      this.fail(Object.assign(new Error(previous?.message ||
        `实时连接已断开（WebSocket ${closeCode}），请重新连接。`), {
        code: previous?.code || previous?.type || "connection_closed",
        close_code: closeCode,
        trace_id: previous?.trace_id,
      }));
    };
  }
  async refresh() {
    try {
      const token = await this.api.getToken(true);
      if (this.readyState !== 1) return;
      if (!token) throw new Error("请重新登录");
      this.socket.send(JSON.stringify({ type: "auth.refresh", token }));
      this.timeout = setTimeout(() => this.fail(new Error("身份续期失败，请重新连接")), 10000);
    } catch (error) { this.fail(error); }
  }
  send(data) {
    if (this.readyState !== 1) return;
    if (this.bufferedAmount > 512 * 1024) { this.fail(new Error("网络连接较慢，请重新连接")); return; }
    const message = JSON.parse(data);
    if (message.type === "input_text" && !message.event_id) message.event_id = crypto.randomUUID();
    this.socket.send(JSON.stringify(message));
  }
  fail(error) {
    if (this.readyState === 3) return;
    this.onmessage?.({ data: JSON.stringify({ type: "error", error: { message: error.message, code: error.code || "client_transport_error",
      close_code: error.close_code, trace_id: error.trace_id } }) });
    this.close();
  }
  close() {
    if (this.readyState === 3) return;
    this.readyState = 3;
    clearTimeout(this.timer); clearTimeout(this.timeout);
    clearInterval(this.heartbeat);
    if (this.socket && this.socket.readyState < 2) this.socket.close();
    this.onclose?.();
  }
}

export function createDemoServices(api) {
  const conversations = new Map();
  const historyListeners = new Set();
  const notifyHistory = () => { for (const listener of historyListeners) listener(); };
  const mutate = async (path, options) => { const result = await api.request(path, options); notifyHistory(); return result; };
  const sockets = new Set();
  const socket = body => {
    const next = new TicketSocket(api, body);
    next.onHistoryChange = notifyHistory;
    sockets.add(next);
    return next;
  };
  return {
    subscribeHistory: listener => { historyListeners.add(listener); return () => historyListeners.delete(listener); },
    models: () => api.request("/demo/models"),
    history: () => api.request("/demo/conversations").then(result => result.conversations),
    messages: id => api.request("/conversations/" + encodeURIComponent(id) + "/messages"),
    async deleteConversation(id) {
      try { await api.request(`/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' }); }
      catch (error) { if (error.status !== 404) throw error; }
      for (const [model, pending] of conversations) {
        pending.then(session => {
          if (session?.id === id && conversations.get(model) === pending) conversations.delete(model);
        }).catch(() => {});
      }
    },
    reset(model) { conversations.delete(model); },
    select(model, id) {
      const pending = api.request("/demo/sessions/" + encodeURIComponent(id) + "/attach", { method: "POST" });
      pending.catch(() => {}); // Report through the next turn, without an unhandled rejection.
      conversations.set(model, pending);
    },
    dispose() { for (const connection of sockets) connection.close(); sockets.clear(); conversations.clear(); historyListeners.clear(); },
    cancelTurn: id => api.request(`/turns/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),
    async streamReply(options) {
      let acc = "";
      try {
        if (!conversations.has(options.model)) {
          const promise = api.request("/demo/sessions", { method: "POST", body: {
            model_id: options.model, mode: "text", controls: controls(options),
          } });
          conversations.set(options.model, promise);
          promise.catch(() => { if (conversations.get(options.model) === promise) conversations.delete(options.model); });
        }
        const conversation = await conversations.get(options.model);
        if (options.signal?.aborted || options.shouldStop?.()) return;
        options.onSession?.(conversation.id);
        const timeZone = browserTimeZone();
        await api.stream(`/conversations/${conversation.id}/turns`, {
          text: options.prompt, client_event_id: crypto.randomUUID(), demo_controls: controls(options),
          ...(timeZone ? { time_zone: timeZone } : {}),
        }, event => {
          if (event.type === "response.created" && event.turn_id) options.onCreated?.(event.turn_id);
          if (event.type === "response.text.delta") { acc += event.delta; if (!options.shouldStop?.()) options.onToken(acc); }
        }, { signal: options.signal });
        options.onDone(acc, { status: "completed" });
      } catch (error) {
        const cancelled = error.name === "AbortError" || error.status === "cancelled";
        options.onDone(acc, { status: cancelled ? "cancelled" : "failed",
          ...(cancelled ? {} : { error: { code: error.code || "connection_error", message: error.message } }) });
      }
    },
    transcribe: body => api.request("/demo/transcribe", { method: "POST", body }),
    courseClient(model) {
      const suffix = "?model=" + encodeURIComponent(model);
      const path = id => "/demo/courses/" + encodeURIComponent(id);
      return {
        list: () => api.request("/demo/courses" + suffix),
        get: id => api.request(path(id) + suffix),
        session: (id, k) => api.request(path(id) + "/session/" + k + suffix),
        ensure: () => api.request("/demo/courses/ensure", { method: "POST", body: { model_id: model } }),
        create: (fields = {}) => mutate("/demo/courses/ensure", { method: "POST", body: { model_id: model, new_experiment: true, ...(fields.request_id ? { request_id: fields.request_id } : {}) } }),
        rename: (id, title) => mutate(path(id) + suffix, { method: "PATCH", body: { title } }),
        remove: id => mutate(path(id) + suffix, { method: "DELETE" }),
        retryArchive: id => mutate(path(id) + '/archive/retry' + suffix, { method: 'POST' }),
        removeCurrentSession: (id, index, token) => mutate(path(id) + "/current-session/" + index + suffix,
          { method: "DELETE", body: { expected_token: token } }),
        removeSession: (id, k) => mutate(path(id) + "/session/" + k + suffix, { method: "DELETE" }),
      };
    },
    realtimeClient(options) {
      return new RealtimeClient({ ...options, transport: "ws", webSocketFactory: () => socket({
        model_id: options.model, mode: "voice", course_id: options.courseId || null, controls: controls(options),
      }) });
    },
    textCourseClient(options) {
      const factory = function () { return socket({
        model_id: options.model, mode: "text", course_id: options.courseId, controls: controls(options),
      }); };
      return new TextCourseClient({ ...options, WebSocketImpl: factory });
    },
  };
}
